import { Component, createRef } from "react"
import PropTypes from "prop-types"
import ClickOutside from "react-click-outside"
import classnames from "classnames"

import colors from "foundation/_colors_export.scss?export"
import displayFlash from "partners/scripts/display_flash"
import fetchAuthenticityToken from "app/scripts/refresh_form_authenticity_token"
import Modal from "shared/components/modal"
import WarningBox from "shared/components/boxes/warning_box"
import AdminOnlyLabel from "partners/components/admin_only_label"

import {
  ServiceListElement,
  orgShape,
  serviceShape,
} from "./selector_components"

import styles from "./service_select_menu.module.scss"

export default class ServiceSelectMenu extends Component {
  state = {
    open: false,
    reimportModalOpen: false,
    reimportFile: null,
    reimportModuleName: null,
    reimportSubmitting: false,
    preserveOauthClient: false,
  }

  fileInputRef = createRef()
  reimportFileInputRef = createRef()

  handleImportClick = e => {
    e.preventDefault()
    this.fileInputRef.current.click()
  }

  handleReimportClick = e => {
    e.preventDefault()
    this.reimportFileInputRef.current.click()
  }

  handleFileSelected = async e => {
    const file = e.target.files[0]
    if (!file) return

    // Reset so the same file can be re-selected
    e.target.value = null

    if (!window.confirm(`Are you sure you want to import a new service into the ${this.props.activeOrg.name} organization?`)) return

    const formData = new FormData()
    formData.append("file", file)
    formData.append("organization_id", this.props.activeOrg.id)

    displayFlash("Importing service…")

    try {
      const response = await fetch("/services/import.json", {
        method: "POST",
        headers: { "X-CSRF-Token": await fetchAuthenticityToken() },
        body: formData,
      })

      const data = await response.json()

      if (!response.ok) {
        displayFlash(data.error || "Import failed. Please try again.", "alert-warning")
        return
      }

      displayFlash("Service imported successfully.")
      window.location.assign(data.redirect_url)
    } catch {
      displayFlash("Import failed. Please try again.", "alert-warning")
    }
  }

  handleReimportFileSelected = async e => {
    const file = e.target.files[0]
    if (!file) return

    e.target.value = null

    // Peek the JSON so the checkbox defaults to checked when the file uses
    // the "placeholder" secret emitted by the for_import export, and so we can
    // show which service the file targets.
    let preserveOauthClient = false
    let reimportModuleName = null
    try {
      const parsed = JSON.parse(await file.text())
      preserveOauthClient = parsed?.authentication?.client_secret === "placeholder"
      reimportModuleName = parsed?.module_name ?? parsed?.slug ?? null
    } catch {
      // Leave defaults; the server will surface the malformed-JSON error on submit.
    }

    this.setState({
      reimportFile: file,
      reimportModuleName,
      preserveOauthClient,
      reimportModalOpen: true,
      open: false,
    })
  }

  handleReimportCancel = () => {
    if (this.state.reimportSubmitting) return
    this.setState({ reimportModalOpen: false, reimportFile: null, reimportModuleName: null })
  }

  handleChooseDifferentFile = () => {
    this.reimportFileInputRef.current.click()
  }

  handlePreserveOauthClientToggle = e => {
    this.setState({ preserveOauthClient: e.target.checked })
  }

  handleReimportConfirm = async () => {
    const { reimportFile, preserveOauthClient } = this.state
    if (!reimportFile) return

    const formData = new FormData()
    formData.append("file", reimportFile)
    formData.append("preserve_oauth_client", preserveOauthClient ? "true" : "false")

    this.setState({ reimportSubmitting: true })
    displayFlash("Re-importing service…")

    try {
      const response = await fetch(this.props.reimportUri, {
        method: "POST",
        headers: { "X-CSRF-Token": await fetchAuthenticityToken() },
        body: formData,
      })

      const data = await response.json()

      if (!response.ok) {
        displayFlash(data.error || "Re-import failed. Please try again.", "alert-warning")
        this.setState({ reimportSubmitting: false })
        return
      }

      displayFlash("Service re-imported successfully.")
      window.location.assign(data.redirect_url)
    } catch {
      displayFlash("Re-import failed. Please try again.", "alert-warning")
      this.setState({ reimportSubmitting: false })
    }
  }

  toggleOpen = e => {
    e.preventDefault()
    e.stopPropagation()
    this.setState({ open: !this.state.open })
  }

  onClickOutside = () => {
    if (this.state.reimportModalOpen) return
    this.setState({
      open: false,
    })
  }

  renderServiceMenuItems() {
    const { activeOrg, currentService } = this.props

    const serviceHasAnOrg = activeOrg.services.some(
      s => s.module_name === currentService.module_name
    )
    const menuServices = serviceHasAnOrg ? activeOrg.services : [currentService]

    if (this.state.open) {
      return [
        ...menuServices.map((service, index) => (
          <ServiceListElement
            key={index}
            service={service}
            shouldRenderChevron={serviceHasAnOrg && index === 0}
            menuIsOpen={this.state.open}
            toggleOpen={this.toggleOpen}
            handleMenuClick={this.handleServiceMenuItemClick}
          />
        )),
        serviceHasAnOrg && (
          <li className="add-item" key={activeOrg.services.length}>
            <a
              onClick={e => {
                e.preventDefault()
                // Are they holding command and wanting to open this in a new tab? Let 'em.
                if (e.metaKey) {
                  return window.open(e.target.href, "_blank")
                }
                window.location.assign(e.target.href)
              }}
              href={`/services/new?org_id=${this.props.activeOrg.id}`}
              title={`Add a new Service to the ${this.props.activeOrg.name} Organization`}
            >
              + Add Service
            </a>
          </li>
        ),
        serviceHasAnOrg && this.props.isAdmin && (
          <li className="add-item" key={`import-${activeOrg.id}`}>
            <a
              onClick={this.handleImportClick}
              href="#"
              title={`Import a Service from JSON into the ${this.props.activeOrg.name} Organization`}
            >
              + Import Service <AdminOnlyLabel />
            </a>
          </li>
        ),
        serviceHasAnOrg && this.props.isAdmin && (
          <li className="add-item" key={`reimport-${activeOrg.id}`}>
            <a
              onClick={this.handleReimportClick}
              href="#"
              title="Re-Import an existing Service from JSON (the service is identified by module_name in the file)"
            >
              + Re-Import Service <AdminOnlyLabel />
            </a>
          </li>
        ),
      ]
    } else if (currentService && currentService.organization) {
      return (
        <ServiceListElement
          service={currentService}
          shouldRenderChevron
          isMakerService={false}
          menuIsOpen={this.state.open}
          toggleOpen={this.toggleOpen}
          handleMenuClick={this.handleServiceMenuItemClick}
        />
      )
    }
  }

  handleServiceMenuItemClick = service => {
    if (this.state.open) {
      if (this.props.currentService.module_name === service.module_name) {
        return this.setState({ open: false })
      }
      return window.location.assign(service.url)
    }
    this.setState({ open: true })
  }

  getColor(org) {
    if (org.color_hsl === "#000") {
      return colors.primaryTextColor
    }
    return org.color_hsl
  }

  renderReimportModal() {
    const {
      reimportFile,
      reimportModuleName,
      reimportSubmitting,
      preserveOauthClient,
      reimportModalOpen,
    } = this.state

    return (
      <Modal
        show={reimportModalOpen}
        closeListener={this.handleReimportCancel}
        headerText="Re-Import Service"
        includeCloseX={false}
        leftJustifyHeader
      >
        <div className={styles.body}>
          <p className={styles.intro}>
            {reimportModuleName ? (
              <>
                This updates <strong>{reimportModuleName}</strong> in place.
              </>
            ) : (
              <>This updates the service identified by <code>module_name</code> in the file, in place.</>
            )}
          </p>

          <WarningBox message="Existing applets, statements, and live channels for this service will be permanently destroyed." />

          <div className={styles["file-row"]}>
            <span className={styles["file-name"]}>
              <strong>File:</strong> {reimportFile ? reimportFile.name : ""}
            </span>
            <button
              type="button"
              className="button-secondary button-cancel-link"
              onClick={this.handleChooseDifferentFile}
              disabled={reimportSubmitting}
            >
              Choose a different file
            </button>
          </div>

          <label className={styles["preserve-label"]}>
            <input
              type="checkbox"
              checked={preserveOauthClient}
              onChange={this.handlePreserveOauthClientToggle}
              disabled={reimportSubmitting}
            />
            {" "}
            Preserve existing OAuth client credentials
            <small className={styles.help}>
              Keeps the service&apos;s current OAuth client instead of using the values
              in the file. Auto-enabled when the file&apos;s <code>client_secret</code> is
              the <code>&quot;placeholder&quot;</code> value from the <code>for_import</code>{" "}
              export.
            </small>
          </label>

          <div className="action-buttons-container">
            <button
              type="button"
              className="button-secondary"
              onClick={this.handleReimportConfirm}
              disabled={reimportSubmitting}
            >
              {reimportSubmitting ? "Re-importing…" : "Re-Import service"}
            </button>
            <button
              type="button"
              className="button-secondary button-cancel-link"
              onClick={this.handleReimportCancel}
              disabled={reimportSubmitting}
            >
              Cancel
            </button>
          </div>
        </div>
      </Modal>
    )
  }

  render() {
    let navClass = classnames("service-dropdown", {
      "services-open": this.state.open,
    })
    return (
      <>
        <ClickOutside onClickOutside={this.onClickOutside}>
          <nav className={navClass}>
            <ul
              className="service-list"
              style={{ backgroundColor: this.getColor(this.props.activeOrg) }}
            >
              {this.renderServiceMenuItems()}
            </ul>
            {this.props.isAdmin && (
              <>
                <input
                  type="file"
                  ref={this.fileInputRef}
                  accept="application/json"
                  style={{ display: "none" }}
                  onChange={this.handleFileSelected}
                />
                <input
                  type="file"
                  ref={this.reimportFileInputRef}
                  accept="application/json"
                  style={{ display: "none" }}
                  onChange={this.handleReimportFileSelected}
                />
              </>
            )}
          </nav>
        </ClickOutside>
        {this.props.isAdmin && this.renderReimportModal()}
      </>
    )
  }
}

ServiceSelectMenu.propTypes = {
  activeOrg: orgShape,
  currentService: serviceShape,
  currentOrganization: orgShape,
  otherId: PropTypes.number,
  baseURI: PropTypes.string,
  makerURI: PropTypes.string,
  reimportUri: PropTypes.string,
  isAdmin: PropTypes.bool,
}
